/* ==========================================================================
   HD MUSCLE — Supplement PDP system
   Vanilla custom elements. No framework, no dependencies beyond the theme's
   own globals (formatMoney, routes, publish/PUB_SUB_EVENTS, cart-drawer).
   Every global it touches is feature-detected, so the file is safe to load
   on any template.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- utils */

  var MOTION_OK = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function money(cents) {
    if (typeof cents !== 'number') return '';
    var format =
      (window.theme && window.theme.settings && window.theme.settings.money_with_currency_format) ||
      (window.Shopify && window.Shopify.money_format) ||
      '${{amount}}';
    if (typeof window.formatMoney === 'function') {
      try {
        return window.formatMoney(cents, format);
      } catch (e) {
        /* fall through */
      }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
  }

  function publishVariant(sectionId, variant) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId: sectionId, variant: variant, html: null } });
    }
  }

  function cartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  /**
   * Adds one or more line items and refreshes the theme's cart drawer /
   * notification without a page reload.
   * @param {Array} items - [{ id, quantity, selling_plan? }]
   */
  function addItems(items) {
    var cart = cartElement();
    var body = { items: items };

    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map(function (s) {
        return s.id;
      });
      body.sections_url = window.location.pathname;
    }

    return fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok || result.data.status) {
          var err = new Error(result.data.description || result.data.message || 'Cart error');
          err.cartResponse = result.data;
          throw err;
        }
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(result.data);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hdm-pdp', cartData: result.data });
        }
        return result.data;
      });
  }

  /* ============================================================== GALLERY */

  if (!customElements.get('hdm-gallery')) {
    customElements.define(
      'hdm-gallery',
      class HdmGallery extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-gallery-track]');
          this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-slide]'));
          this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-thumb]'));
          this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-dot]'));
          this.counter = this.querySelector('[data-gallery-current]');
          this.zoomBtn = this.querySelector('[data-gallery-zoom]');
          this.lightbox = this.querySelector('[data-gallery-lightbox]');
          this.lightboxImg = this.querySelector('[data-gallery-lightbox-image]');
          this.prevBtn = this.querySelector('[data-gallery-prev]');
          this.nextBtn = this.querySelector('[data-gallery-next]');
          this.moreBtn = this.querySelector('[data-gallery-more]');
          this.index = 0;

          if (!this.track || !this.slides.length) return;

          this.onScroll = this.debounce(this.syncFromScroll.bind(this), 80);
          this.track.addEventListener('scroll', this.onScroll, { passive: true });

          this.thumbs.forEach(
            function (thumb, i) {
              thumb.addEventListener('click', this.goTo.bind(this, i, true));
            }.bind(this)
          );
          this.dots.forEach(
            function (dot, i) {
              dot.addEventListener('click', this.goTo.bind(this, i, true));
            }.bind(this)
          );

          if (this.prevBtn) this.prevBtn.addEventListener('click', this.step.bind(this, -1));
          if (this.nextBtn) this.nextBtn.addEventListener('click', this.step.bind(this, 1));
          if (this.moreBtn) {
            this.moreBtn.addEventListener(
              'click',
              function () {
                this.querySelectorAll('[data-gallery-hidden-thumb]').forEach(function (el) {
                  el.hidden = false;
                  el.removeAttribute('data-gallery-hidden-thumb');
                });
                this.moreBtn.remove();
              }.bind(this)
            );
          }

          this.addEventListener('keydown', this.onKeydown.bind(this));

          if (this.dataset.zoom === 'true') this.initZoom();
          if (this.zoomBtn && this.lightbox && this.lightboxImg) this.initLightbox();
          if (this.dataset.animate === 'true' && MOTION_OK) this.initEntrance();

          this.updateNav();
        }

        disconnectedCallback() {
          if (this.track && this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
          /* The lightbox was moved to <body>, so it does not leave with the
             section when the theme editor re-renders it. */
          if (this.lightbox && this.lightbox.parentElement === document.body) {
            this.lightbox.remove();
            document.body.style.overflow = '';
          }
        }

        debounce(fn, wait) {
          var t;
          return function () {
            clearTimeout(t);
            t = setTimeout(fn, wait);
          };
        }

        onKeydown(event) {
          if (!event.target.closest('[data-gallery-thumb]')) return;
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.step(1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.step(-1);
          }
        }

        initEntrance() {
          var target = this.querySelector('[data-gallery-animate]') || this;
          target.classList.add('hdm-anim-in');
          var observer = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                if (!entry.isIntersecting) return;
                requestAnimationFrame(function () {
                  entry.target.classList.add('hdm-anim-in--done');
                });
                observer.unobserve(entry.target);
              });
            },
            { threshold: 0.15 }
          );
          observer.observe(target);
        }

        initZoom() {
          this.slides.forEach(function (slide) {
            if (slide.dataset.mediaType !== 'image') return;
            slide.addEventListener('mousemove', function (event) {
              var rect = slide.getBoundingClientRect();
              slide.style.setProperty('--hdm-zoom-x', ((event.clientX - rect.left) / rect.width) * 100 + '%');
              slide.style.setProperty('--hdm-zoom-y', ((event.clientY - rect.top) / rect.height) * 100 + '%');
            });
          });
        }

        syncFromScroll() {
          var width = this.track.clientWidth;
          if (!width) return;
          var next = Math.round(this.track.scrollLeft / width);
          if (next === this.index) return;
          this.index = Math.max(0, Math.min(next, this.slides.length - 1));
          this.updateNav();
          this.pauseMedia();
        }

        step(direction) {
          this.goTo(this.index + direction, true);
        }

        goTo(index, scroll) {
          if (index < 0 || index >= this.slides.length) return;
          this.index = index;
          if (scroll !== false) {
            this.track.scrollTo({ left: this.track.clientWidth * index, behavior: MOTION_OK ? 'smooth' : 'auto' });
          }
          this.updateNav();
          this.pauseMedia();
        }

        /** Jump to the slide bound to a Shopify media id (variant featured media). */
        selectMedia(mediaId) {
          if (!mediaId) return;
          var target = this.slides.findIndex(function (slide) {
            return String(slide.dataset.mediaId) === String(mediaId);
          });
          if (target > -1 && target !== this.index) this.goTo(target, true);
        }

        pauseMedia() {
          this.querySelectorAll('video, iframe').forEach(
            function (node, i) {
              var slide = node.closest('[data-gallery-slide]');
              if (!slide || slide === this.slides[this.index]) return;
              if (node.tagName === 'VIDEO' && typeof node.pause === 'function') node.pause();
              if (node.tagName === 'IFRAME' && node.contentWindow) {
                node.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
              }
            }.bind(this)
          );
        }

        updateNav() {
          this.thumbs.forEach(
            function (thumb, i) {
              thumb.setAttribute('aria-current', i === this.index ? 'true' : 'false');
              var row = thumb.parentElement;
              if (i === this.index && row && row.hidden && this.moreBtn) this.moreBtn.click();
            }.bind(this)
          );
          this.dots.forEach(
            function (dot, i) {
              dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
            }.bind(this)
          );
          if (this.counter) this.counter.textContent = String(this.index + 1);
          if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
          if (this.nextBtn) this.nextBtn.disabled = this.index === this.slides.length - 1;
        }

        /* Full-screen view of whichever slide is showing. The source is taken
           from the slide's own <img> at its largest srcset candidate, so the
           lightbox costs no extra markup and no second set of image URLs. */
        initLightbox() {
          var self = this;

          this.zoomBtn.addEventListener('click', function () {
            var slide = self.slides[self.index];
            var img = slide && slide.querySelector('img');
            if (!img) return;
            /* The gallery lives inside a sticky column, and a sticky ancestor
               creates a stacking context. Anything positioned inside one is
               trapped there no matter how high its z-index goes, which put the
               lightbox underneath the variant pills and the Add to cart button.
               Moving it to <body> on first open takes it out of that context
               entirely. */
            if (self.lightbox.parentElement !== document.body) {
              document.body.appendChild(self.lightbox);
            }
            self.lightboxImg.src = img.currentSrc || img.src;
            self.lightboxImg.alt = img.alt || '';
            self.lightbox.setAttribute('open', '');
            document.body.style.overflow = 'hidden';
            var close = self.lightbox.querySelector('[data-gallery-lightbox-close]');
            if (close) close.focus();
          });

          Array.prototype.forEach.call(
            this.lightbox.querySelectorAll('[data-gallery-lightbox-close]'),
            function (btn) {
              btn.addEventListener('click', function () { self.closeLightbox(); });
            }
          );

          document.addEventListener('keydown', function (event) {
            if (event.key === 'Escape' && self.lightbox.hasAttribute('open')) self.closeLightbox();
          });
        }

        closeLightbox() {
          if (!this.lightbox) return;
          this.lightbox.removeAttribute('open');
          this.lightboxImg.src = '';
          document.body.style.overflow = '';
          if (this.zoomBtn) this.zoomBtn.focus();
        }
      }
    );
  }

  /* ============================================================== BUY BOX */

  if (!customElements.get('hdm-buy-box')) {
    customElements.define(
      'hdm-buy-box',
      class HdmBuyBox extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.sectionId;
          this.productUrl = this.dataset.productUrl;
          this.updateUrl = this.dataset.updateUrl !== 'false';

          /* Never let bad data take the whole buy box down. A parse failure
             used to throw here, so nothing below ever ran and the page shipped
             with dead quantity buttons and dead variant pills. */
          var data = this.querySelector('[data-hdm-variants]');
          this.variants = [];
          if (data) {
            try {
              this.variants = JSON.parse(data.textContent) || [];
            } catch (error) {
              console.error('[hdm-buy-box] variant data is not valid JSON:', error);
            }
          }
          this.optionInputs = Array.prototype.slice.call(this.querySelectorAll('[data-option-position]'));
          // form, idInput and qtyInput are getters, not cached properties.
          // A subscription app (Kaching) re-renders the product form on
          // subscription products, which detaches anything cached at startup —
          // writes then land on orphaned nodes and never reach the real form.
          this.planInput = this.querySelector('input[name="selling_plan"]');
          this.submit = this.querySelector('[data-atc-button]');
          this.gallery = document.querySelector('hdm-gallery[data-section-id="' + this.sectionId + '"]');

          this.current = this.variants.find(
            function (v) {
              return String(v.id) === String(this.idInput && this.idInput.value);
            }.bind(this)
          ) || this.variants[0];

          this.bindOptions();
          this.bindQuantity();
          this.bindPlans();
          this.bindSticky();

          // Reflect the initial state so server-rendered and JS-derived UI agree.
          this.render();
        }

        /* ---------------------------------------------------------- options */

        bindOptions() {
          this.optionInputs.forEach(
            function (input) {
              input.addEventListener('change', this.onOptionChange.bind(this));
            }.bind(this)
          );
        }

        get selectedValues() {
          var values = [];
          this.optionInputs.forEach(function (input) {
            var pos = parseInt(input.dataset.optionPosition, 10) - 1;
            if (input.tagName === 'SELECT') {
              values[pos] = input.value;
            } else if (input.checked) {
              values[pos] = input.value;
            }
          });
          return values;
        }

        onOptionChange(event) {
          var changed = event.target;
          var position = parseInt(changed.dataset.optionPosition, 10) - 1;
          var value = changed.value;
          var match = this.findVariant(this.selectedValues);

          // The chosen combination may not exist — fall back to the closest
          // variant that honours the option the customer just clicked.
          if (!match) {
            match = this.closestVariant(position, value);
            if (match) this.syncInputs(match);
          }

          this.current = match || null;
          this.render();
        }

        findVariant(values) {
          return (
            this.variants.find(function (variant) {
              return variant.options.every(function (option, i) {
                return values[i] === undefined || option === values[i];
              });
            }) || null
          );
        }

        closestVariant(position, value) {
          var values = this.selectedValues;
          var candidates = this.variants.filter(function (variant) {
            return variant.options[position] === value;
          });
          // Prefer an available variant that keeps as many current picks as possible.
          var scored = candidates
            .map(function (variant) {
              var score = 0;
              variant.options.forEach(function (option, i) {
                if (i !== position && option === values[i]) score++;
              });
              return { variant: variant, score: score + (variant.available ? 100 : 0) };
            })
            .sort(function (a, b) {
              return b.score - a.score;
            });
          return scored.length ? scored[0].variant : null;
        }

        syncInputs(variant) {
          this.optionInputs.forEach(function (input) {
            var pos = parseInt(input.dataset.optionPosition, 10) - 1;
            if (input.tagName === 'SELECT') {
              input.value = variant.options[pos];
            } else {
              input.checked = input.value === variant.options[pos];
            }
          });
        }

        /** Grey out option values that produce no purchasable variant. */
        updateOptionAvailability() {
          var values = this.selectedValues;

          this.optionInputs.forEach(
            function (input) {
              var pos = parseInt(input.dataset.optionPosition, 10) - 1;

              var check = function (candidate) {
                return this.variants.some(function (variant) {
                  if (variant.options[pos] !== candidate) return false;
                  return variant.options.every(function (option, i) {
                    return i === pos || values[i] === undefined || option === values[i];
                  }) && variant.available;
                });
              }.bind(this);

              if (input.tagName === 'SELECT') {
                Array.prototype.forEach.call(input.options, function (option) {
                  var ok = check(option.value);
                  option.classList.toggle('hdm-option--unavailable', !ok);
                  option.textContent = option.dataset.label + (ok ? '' : ' — ' + (window.variantStrings ? window.variantStrings.soldOut : 'Sold out'));
                });
              } else {
                var wrapper = input.closest('.hdm-swatch');
                if (wrapper) wrapper.classList.toggle('hdm-swatch--unavailable', !check(input.value));
              }
            }.bind(this)
          );

          // Reflect the picked value next to each option label.
          this.querySelectorAll('[data-selected-value]').forEach(function (node) {
            var pos = parseInt(node.dataset.selectedValue, 10) - 1;
            node.textContent = values[pos] || '';
          });
        }

        /* -------------------------------------------------------- quantity */

        /* Delegated from the component rather than bound to each button, so the
           controls keep working if a subscription app re-renders the form and
           replaces them. Binding per button loses the listener with the node. */
        bindQuantity() {
          this.addEventListener(
            'click',
            function (event) {
              var button = event.target.closest('[data-qty-change]');
              if (!button || !this.contains(button)) return;
              var input = this.qtyInput;
              if (!input) return;

              var delta = parseInt(button.dataset.qtyChange, 10);
              var next = (parseInt(input.value, 10) || 1) + delta;
              var min = parseInt(input.min, 10) || 1;
              var max = input.max ? parseInt(input.max, 10) : Infinity;
              input.value = Math.max(min, Math.min(next, max));
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }.bind(this)
          );

          this.addEventListener(
            'change',
            function (event) {
              if (event.target.matches('[data-qty-input]')) this.render();
            }.bind(this)
          );
        }

        get quantity() {
          return Math.max(1, parseInt(this.qtyInput ? this.qtyInput.value : 1, 10) || 1);
        }

        /* ---------------------------------------------------- selling plans */

        bindPlans() {
          this.planRadios = Array.prototype.slice.call(this.querySelectorAll('[data-plan-radio]'));
          this.planSelects = Array.prototype.slice.call(this.querySelectorAll('[data-plan-select]'));

          // When the section's own subscription UI is switched off, a
          // subscription app (Kaching here) owns it instead and writes the
          // chosen plan into the product form. In that case we read the form
          // rather than our own radios, and never write to it.
          this.ownsPlans = this.planRadios.length > 0;
          if (!this.ownsPlans) {
            this.watchExternalPlan();
          }

          this.planRadios.forEach(
            function (radio) {
              radio.addEventListener('change', this.render.bind(this));
            }.bind(this)
          );
          this.planSelects.forEach(
            function (select) {
              select.addEventListener(
                'change',
                function () {
                  var radio = this.planRadios.find(function (r) {
                    return r.dataset.groupId === select.dataset.groupId;
                  });
                  if (radio) radio.checked = true;
                  this.render();
                }.bind(this)
              );
            }.bind(this)
          );
        }

        /**
         * A subscription app can set the plan programmatically, which fires no
         * change event. Listen for events where we can, and poll as a backstop
         * so the price and button always follow the app's selection.
         */
        watchExternalPlan() {
          var read = function () {
            var input = (this.form || this).querySelector('[name="selling_plan"]');
            return input ? String(input.value || '') : '';
          }.bind(this);

          this.externalPlan = read();

          var check = function () {
            var next = read();
            if (next === this.externalPlan) return;
            this.externalPlan = next;
            this.render();
          }.bind(this);

          if (this.form) this.form.addEventListener('change', check);
          document.addEventListener('change', check);
          this.externalPlanTimer = setInterval(check, 400);
        }

        get selectedPlanId() {
          if (!this.ownsPlans) {
            var input = (this.form || this).querySelector('[name="selling_plan"]');
            var value = input ? String(input.value || '') : '';
            return value === '' ? null : value;
          }
          if (!this.planRadios || !this.planRadios.length) return null;
          var checked = this.planRadios.find(function (radio) {
            return radio.checked;
          });
          if (!checked || checked.value === 'onetime') return null;
          var select = this.planSelects.find(function (s) {
            return s.dataset.groupId === checked.dataset.groupId;
          });
          return select ? select.value : checked.value;
        }

        allocationFor(planId) {
          if (!this.current || !planId) return null;
          return (
            (this.current.selling_plan_allocations || []).find(function (allocation) {
              return String(allocation.selling_plan_id) === String(planId);
            }) || null
          );
        }

        /** Prices shown on each purchase-type row, for the current variant. */
        updatePlanPricing() {
          if (!this.planRadios || !this.planRadios.length) return;

          this.planRadios.forEach(
            function (radio) {
              var row = radio.closest('.hdm-plan');
              var priceNode = row ? row.querySelector('[data-plan-price]') : null;
              if (!priceNode) return;

              if (radio.value === 'onetime') {
                priceNode.textContent = this.current ? money(this.current.price) : '';
                return;
              }

              var select = this.planSelects.find(function (s) {
                return s.dataset.groupId === radio.dataset.groupId;
              });
              var planId = select ? select.value : radio.value;
              var allocation = this.allocationFor(planId);

              if (!allocation) {
                // Plan not offered on this variant — hide the row entirely.
                if (row) row.classList.add('hdm-hidden');
                if (radio.checked) {
                  var onetime = this.planRadios.find(function (r) {
                    return r.value === 'onetime';
                  });
                  if (onetime) onetime.checked = true;
                }
                return;
              }

              if (row) row.classList.remove('hdm-hidden');
              var html = '';
              if (allocation.compare_at_price && allocation.compare_at_price > allocation.price) {
                html += '<s>' + money(allocation.compare_at_price) + '</s>';
              }
              html += money(allocation.price);
              priceNode.innerHTML = html;
            }.bind(this)
          );
        }

        /* -------------------------------------------------- sticky mobile bar */

        bindSticky() {
          this.sticky = this.querySelector('[data-sticky-bar]');
          if (!this.sticky) return;

          var anchor = this.querySelector('[data-atc-row]');
          if (!anchor || !('IntersectionObserver' in window)) return;

          // Watch the gallery as well as the button, so the bar can appear as
          // soon as the image is gone rather than waiting for the whole
          // information column to scroll by.
          var gallery = document.querySelector('hdm-gallery[data-section-id="' + this.sectionId + '"]');

          this.stickyState = { pastGallery: false, atcVisible: true };

          var apply = function () {
            var show = this.stickyState.pastGallery && !this.stickyState.atcVisible;
            this.sticky.classList.toggle('is-visible', show);
          }.bind(this);

          this.stickyObserver = new IntersectionObserver(
            function (entries) {
              entries.forEach(
                function (entry) {
                  if (entry.target === anchor) {
                    this.stickyState.atcVisible = entry.isIntersecting;
                    if (!gallery && entry.boundingClientRect.top >= 0) {
                      // No gallery to key off: fall back to the old behaviour.
                      this.stickyState.pastGallery = false;
                    }
                  } else {
                    this.stickyState.pastGallery =
                      !entry.isIntersecting && entry.boundingClientRect.top < 0;
                  }
                }.bind(this)
              );
              apply();
            }.bind(this),
            { threshold: 0 }
          );

          this.stickyObserver.observe(anchor);
          if (gallery) {
            this.stickyObserver.observe(gallery);
          } else {
            this.stickyState.pastGallery = true;
          }

          var stickyButton = this.sticky.querySelector('[data-sticky-add]');
          if (stickyButton && this.form) {
            stickyButton.addEventListener(
              'click',
              function () {
                if (typeof this.form.requestSubmit === 'function') {
                  this.form.requestSubmit();
                } else {
                  this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                }
              }.bind(this)
            );
          }
        }

        /* Live DOM lookups — see connectedCallback. Cheap, and immune to any
           app that replaces the form subtree after we initialise. */
        get form() {
          return this.querySelector('form[data-type="add-to-cart-form"]');
        }
        get idInput() {
          var form = this.form;
          return form ? form.querySelector('input[name="id"]') : null;
        }
        get qtyInput() {
          return this.querySelector('[data-qty-input]');
        }

        disconnectedCallback() {
          if (this.externalPlanTimer) clearInterval(this.externalPlanTimer);
          if (this.stickyObserver) this.stickyObserver.disconnect();
        }

        /* ----------------------------------------------------------- render */

        render() {
          var variant = this.current;
          var planId = this.selectedPlanId;
          var allocation = this.allocationFor(planId);
          var unitPrice = allocation ? allocation.price : variant ? variant.price : 0;
          var compareAt = allocation
            ? allocation.compare_at_price
            : variant
            ? variant.compare_at_price
            : null;

          this.updateOptionAvailability();
          this.updatePlanPricing();

          /* Hidden inputs -------------------------------------------------- */
          /* Every [name="id"] in the form, not just the first. A subscription
             app can inject its own, and whichever one the browser serialises
             has to carry the variant the customer actually chose. */
          var form = this.form;
          if (form) {
            var idInputs = form.querySelectorAll('input[name="id"]');
            Array.prototype.forEach.call(idInputs, function (input) {
              input.value = variant ? variant.id : '';
              input.disabled = !variant;
            });
          }
          // Only write the plan when this section owns the selector; otherwise
          // the app's value would be clobbered on every render.
          if (this.planInput && this.ownsPlans) this.planInput.value = planId || '';

          /* Price ---------------------------------------------------------- */
          var priceCurrent = this.querySelector('[data-price-current]');
          var priceCompare = this.querySelector('[data-price-compare]');
          var priceSave = this.querySelector('[data-price-save]');

          var onSale = !!(compareAt && compareAt > unitPrice);
          var priceWrap = this.querySelector('.hdm-price');
          if (priceWrap) priceWrap.classList.toggle('is-on-sale', onSale);

          if (priceCurrent) priceCurrent.textContent = variant ? money(unitPrice) : '';
          if (priceCompare) {
            priceCompare.textContent = onSale ? money(compareAt) : '';
            priceCompare.classList.toggle('hdm-hidden', !onSale);
          }
          if (priceSave) {
            priceSave.classList.toggle('hdm-hidden', !onSale);
            if (onSale) {
              // Percent reads punchier on a big discount, amount on a small one;
              // the template decides which.
              if (priceSave.dataset.savingsFormat === 'percent') {
                priceSave.textContent =
                  'Save ' + Math.round(((compareAt - unitPrice) / compareAt) * 100) + '%';
              } else {
                priceSave.textContent = 'Save ' + money(compareAt - unitPrice);
              }
            }
          }

          // Was-price inside the button, so the discount is visible at the
          // point of action too.
          var atcCompare = this.querySelector('[data-atc-compare]');
          if (atcCompare) {
            atcCompare.textContent = onSale ? money(compareAt) : '';
            atcCompare.classList.toggle('hdm-hidden', !onSale);
          }

          /* Unit price (e.g. "$0.25 / serving") ---------------------------- */
          var unitNode = this.querySelector('[data-price-unit]');
          if (unitNode) {
            var servings = variant && variant.servings ? parseFloat(variant.servings) : 0;
            if (servings > 0) {
              unitNode.textContent = money(Math.round(unitPrice / servings)) + ' ' + (unitNode.dataset.unitSuffix || 'per serving');
              unitNode.classList.remove('hdm-hidden');
            } else {
              unitNode.classList.add('hdm-hidden');
            }
          }

          /* Add to cart ---------------------------------------------------- */
          if (this.submit) {
            var label = this.submit.querySelector('[data-atc-label]');
            var priceLabel = this.submit.querySelector('[data-atc-price]');
            var strings = window.variantStrings || {};
            var unavailable = !variant;
            var soldOut = variant && !variant.available;

            this.submit.disabled = unavailable || soldOut;
            this.submit.setAttribute('aria-disabled', String(unavailable || soldOut));

            if (label) {
              label.textContent = unavailable
                ? strings.unavailable || 'Unavailable'
                : soldOut
                ? strings.soldOut || 'Sold out'
                : this.submit.dataset.preorder === 'true'
                ? strings.preOrder || 'Pre-order'
                : this.submit.dataset.defaultLabel || strings.addToCart || 'Add to cart';
            }
            if (priceLabel) {
              var show = !unavailable && !soldOut;
              priceLabel.textContent = show ? money(unitPrice * this.quantity) : '';
              priceLabel.classList.toggle('hdm-hidden', !show);
              var dot = this.submit.querySelector('[data-atc-dot]');
              if (dot) dot.classList.toggle('hdm-hidden', !show);
            }
          }

          /* Sticky bar ------------------------------------------------------ */
          if (this.sticky) {
            var sPrice = this.sticky.querySelector('[data-sticky-price]');
            var sVariant = this.sticky.querySelector('[data-sticky-variant]');
            var sButton = this.sticky.querySelector('[data-sticky-add]');
            if (sPrice) sPrice.textContent = variant ? money(unitPrice * this.quantity) : '';
            if (sVariant) {
              var summary = variant && variant.title && variant.title !== 'Default Title' ? variant.title : '';
              sVariant.textContent = summary;
              sVariant.classList.toggle('hdm-hidden', !summary);
            }
            var sImage = this.sticky.querySelector('[data-sticky-image]');
            if (sImage && variant && variant.featured_image) {
              sImage.src = variant.featured_image;
            }
            if (sButton) {
              sButton.disabled = !variant || !variant.available;
              var sLabel = sButton.querySelector('[data-sticky-label]');
              if (sLabel) {
                sLabel.textContent = !variant
                  ? (window.variantStrings || {}).unavailable || 'Unavailable'
                  : !variant.available
                  ? (window.variantStrings || {}).soldOut || 'Sold out'
                  : sButton.dataset.defaultLabel || 'Add to cart';
              }
            }
          }

          /* Media + URL ----------------------------------------------------- */
          if (variant && variant.featured_media_id && this.gallery) {
            this.gallery.selectMedia(variant.featured_media_id);
          }
          if (variant && this.updateUrl && window.history && window.history.replaceState) {
            window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
          }

          publishVariant(this.sectionId, variant);
          emit('hdm:variant:change', { sectionId: this.sectionId, variant: variant, sellingPlanId: planId });
        }
      }
    );
  }

  /* ================================================================= TABS */

  if (!customElements.get('hdm-details-tabs')) {
    customElements.define(
      'hdm-details-tabs',
      class HdmDetailsTabs extends HTMLElement {
        connectedCallback() {
          this.tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
          this.panels = Array.prototype.slice.call(this.querySelectorAll('[role="tabpanel"]'));

          this.tabs.forEach(
            function (tab, i) {
              tab.addEventListener('click', this.select.bind(this, i));
              tab.addEventListener('keydown', this.onKeydown.bind(this, i));
            }.bind(this)
          );

          this.initAccordions();
          this.initModal();
        }

        select(index, event) {
          if (event) event.preventDefault();

          this.tabs.forEach(function (tab, i) {
            var on = i === index;
            tab.setAttribute('aria-selected', String(on));
            tab.tabIndex = on ? 0 : -1;
          });

          // Tab order is template-driven, so it can differ from panel DOM
          // order. Pair them by aria-controls rather than by index.
          var activeId = this.tabs[index].getAttribute('aria-controls');
          this.panels.forEach(function (panel) {
            panel.hidden = panel.id !== activeId;
          });

          if (event) this.tabs[index].focus();
        }

        onKeydown(index, event) {
          var last = this.tabs.length - 1;
          var next = null;
          if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
          if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = last;
          if (next === null) return;
          event.preventDefault();
          this.select(next, event);
        }

        initAccordions() {
          this.querySelectorAll('[data-accordion-trigger]').forEach(
            function (trigger) {
              trigger.addEventListener(
                'click',
                function () {
                  var expanded = trigger.getAttribute('aria-expanded') === 'true';
                  var group = trigger.closest('[data-accordion-group]');

                  if (group) {
                    group.querySelectorAll('[data-accordion-trigger]').forEach(function (other) {
                      other.setAttribute('aria-expanded', 'false');
                      var otherPanel = document.getElementById(other.getAttribute('aria-controls'));
                      if (otherPanel) otherPanel.hidden = true;
                    });
                  }

                  trigger.setAttribute('aria-expanded', String(!expanded));
                  var panel = document.getElementById(trigger.getAttribute('aria-controls'));
                  if (panel) panel.hidden = expanded;
                }
              );
            }
          );
        }

        initModal() {
          var opener = this.querySelector('[data-facts-open]');
          var modal = this.querySelector('[data-facts-modal]');
          if (!opener || !modal) return;

          var lastFocus = null;
          var close = function () {
            modal.removeAttribute('open');
            document.body.classList.remove('overflow-hidden');
            if (lastFocus) lastFocus.focus();
          };

          opener.addEventListener('click', function () {
            lastFocus = document.activeElement;
            modal.setAttribute('open', '');
            document.body.classList.add('overflow-hidden');
            var closeBtn = modal.querySelector('[data-facts-close]');
            if (closeBtn) closeBtn.focus();
          });

          modal.querySelectorAll('[data-facts-close]').forEach(function (button) {
            button.addEventListener('click', close);
          });
          document.addEventListener('keydown', function (event) {
            if (event.key === 'Escape' && modal.hasAttribute('open')) close();
          });
        }
      }
    );
  }

  /* ================================================ COMPLETE YOUR STACK */

  if (!customElements.get('hdm-stack')) {
    customElements.define(
      'hdm-stack',
      class HdmStack extends HTMLElement {
        connectedCallback() {
          this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-stack-card]'));
          this.buttons = Array.prototype.slice.call(this.querySelectorAll('[data-stack-add]'));
          this.status = this.querySelector('[data-stack-status]');

          this.cards.forEach(
            function (card) {
              var raw = card.querySelector('[data-stack-variants]');
              card.hdmVariants = raw ? JSON.parse(raw.textContent) : [];
              card.hdmVariant =
                card.hdmVariants.find(function (v) {
                  return String(v.id) === String(card.dataset.selectedVariant);
                }) || card.hdmVariants[0];

              var toggle = card.querySelector('[data-stack-toggle]');
              if (toggle) {
                var initial = toggle.querySelector('[data-stack-toggle-label]');
                if (initial) toggle.dataset.initialLabel = initial.textContent.trim();
                if (!toggle.disabled) {
                  toggle.addEventListener('click', this.onToggle.bind(this, card, toggle));
                }
              }

              card.querySelectorAll('[data-stack-option]').forEach(
                function (select) {
                  select.addEventListener('change', this.onOptionChange.bind(this, card));
                }.bind(this)
              );

              this.renderCard(card);
            }.bind(this)
          );

          this.buttons.forEach(
            function (button) {
              button.addEventListener('click', this.addSelected.bind(this));
            }.bind(this)
          );

          this.updateTotals();
        }

        onToggle(card, toggle) {
          var on = toggle.getAttribute('aria-pressed') === 'true';
          toggle.setAttribute('aria-pressed', String(!on));
          card.classList.toggle('is-selected', !on);
          this.updateTotals();
        }

        onOptionChange(card) {
          var values = [];
          card.querySelectorAll('[data-stack-option]').forEach(function (select) {
            values[parseInt(select.dataset.optionPosition, 10) - 1] = select.value;
          });

          var match = card.hdmVariants.find(function (variant) {
            return variant.options.every(function (option, i) {
              return values[i] === undefined || option === values[i];
            });
          });

          if (!match) {
            // Snap back to a real combination so the card can never sit in a
            // state that cannot be added to the cart.
            match =
              card.hdmVariants.find(function (variant) {
                return variant.available;
              }) || card.hdmVariants[0];
            if (match) {
              card.querySelectorAll('[data-stack-option]').forEach(function (select) {
                select.value = match.options[parseInt(select.dataset.optionPosition, 10) - 1];
              });
            }
          }

          card.hdmVariant = match;
          this.renderCard(card);
          this.updateTotals();
        }

        renderCard(card) {
          var variant = card.hdmVariant;
          var priceNode = card.querySelector('[data-stack-price]');
          var toggle = card.querySelector('[data-stack-toggle]');
          var image = card.querySelector('[data-stack-image]');

          if (priceNode) {
            if (!variant) {
              priceNode.textContent = '';
            } else {
              var html = '';
              if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                html += '<s>' + money(variant.compare_at_price) + '</s>';
              }
              html += money(variant.price);
              priceNode.innerHTML = html;
            }
          }

          if (image && variant && variant.featured_image) {
            image.src = variant.featured_image;
            if (variant.featured_image_srcset) image.srcset = variant.featured_image_srcset;
          }

          var unavailable = !variant || !variant.available;
          var soldOutNote = card.querySelector('[data-stack-soldout]');
          if (soldOutNote) soldOutNote.classList.toggle('hdm-hidden', !unavailable);

          if (toggle && card.dataset.current !== 'true') {
            toggle.disabled = unavailable;
            if (unavailable && toggle.getAttribute('aria-pressed') === 'true') {
              toggle.setAttribute('aria-pressed', 'false');
              card.classList.remove('is-selected');
            }
            var label = toggle.querySelector('[data-stack-toggle-label]');
            if (label) {
              label.textContent = unavailable
                ? toggle.dataset.soldOutLabel || 'Sold out'
                : toggle.dataset.initialLabel || label.textContent;
            }
          }
        }

        get selection() {
          return this.cards
            .filter(function (card) {
              if (card.dataset.current === 'true') return false;
              var toggle = card.querySelector('[data-stack-toggle]');
              return toggle && toggle.getAttribute('aria-pressed') === 'true' && card.hdmVariant && card.hdmVariant.available;
            })
            .map(function (card) {
              return {
                id: card.hdmVariant.id,
                quantity: parseInt(card.dataset.quantity, 10) || 1,
                price: card.hdmVariant.price,
              };
            });
        }

        updateTotals() {
          var items = this.selection;
          var count = items.reduce(function (sum, item) {
            return sum + item.quantity;
          }, 0);
          var total = items.reduce(function (sum, item) {
            return sum + item.price * item.quantity;
          }, 0);

          this.buttons.forEach(function (button) {
            var label = button.querySelector('[data-stack-add-label]');
            var priceNode = button.querySelector('[data-stack-add-price]');
            button.disabled = count === 0;
            if (label) {
              label.textContent = count === 0
                ? button.dataset.emptyLabel || 'Select items to add'
                : (button.dataset.addLabel || 'Add [count] items to cart')
                    .replace('[count]', count)
                    .replace('[item_word]', count === 1 ? 'item' : 'items');
            }
            if (priceNode) {
              priceNode.textContent = count === 0 ? '' : money(total);
              priceNode.classList.toggle('hdm-hidden', count === 0);
            }
          });
        }

        setStatus(message, isError) {
          if (!this.status) return;
          this.status.textContent = message || '';
          this.status.classList.toggle('hdm-stack__status--error', !!isError);
        }

        addSelected(event) {
          var button = event.currentTarget;
          var items = this.selection;
          if (!items.length) return;

          button.classList.add('loading');
          button.disabled = true;
          this.setStatus('');

          addItems(
            items.map(function (item) {
              return { id: item.id, quantity: item.quantity };
            })
          )
            .then(
              function () {
                this.cards.forEach(function (card) {
                  var toggle = card.querySelector('[data-stack-toggle]');
                  if (toggle && card.dataset.current !== 'true') {
                    toggle.setAttribute('aria-pressed', 'false');
                    card.classList.remove('is-selected');
                  }
                });
                this.updateTotals();
              }.bind(this)
            )
            .catch(
              function (error) {
                // One line may have gone out of stock between render and click:
                // retry with the remaining items rather than failing the batch.
                var response = error.cartResponse || {};
                var failedId = response.id ? String(response.id) : null;
                var remaining = items.filter(function (item) {
                  return String(item.id) !== failedId;
                });

                if (failedId && remaining.length) {
                  return addItems(
                    remaining.map(function (item) {
                      return { id: item.id, quantity: item.quantity };
                    })
                  )
                    .then(
                      function () {
                        this.setStatus(
                          'One item is no longer available and was skipped. The rest of your stack was added.',
                          true
                        );
                      }.bind(this)
                    )
                    .catch(
                      function () {
                        this.setStatus(error.message || 'Something went wrong. Please try again.', true);
                      }.bind(this)
                    );
                }

                this.setStatus(error.message || 'Something went wrong. Please try again.', true);
              }.bind(this)
            )
            .finally(
              function () {
                button.classList.remove('loading');
                this.updateTotals();
              }.bind(this)
            );
        }
      }
    );
  }

  /* ============================================== YOU MAY ALSO LIKE / misc */

  if (!customElements.get('hdm-carousel')) {
    customElements.define(
      'hdm-carousel',
      class HdmCarousel extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-carousel-track]');
          this.prev = this.querySelector('[data-carousel-prev]');
          this.next = this.querySelector('[data-carousel-next]');
          if (!this.track) return;

          if (this.prev) this.prev.addEventListener('click', this.scrollBy.bind(this, -1));
          if (this.next) this.next.addEventListener('click', this.scrollBy.bind(this, 1));
          this.track.addEventListener('scroll', this.update.bind(this), { passive: true });

          this.querySelectorAll('[data-quick-add]').forEach(
            function (button) {
              button.addEventListener('click', this.quickAdd.bind(this, button));
            }.bind(this)
          );

          this.update();
        }

        scrollBy(direction) {
          var first = this.track.firstElementChild;
          var step = first ? first.getBoundingClientRect().width + 12 : this.track.clientWidth;
          this.track.scrollBy({ left: step * direction, behavior: MOTION_OK ? 'smooth' : 'auto' });
        }

        update() {
          if (!this.prev || !this.next) return;
          var max = this.track.scrollWidth - this.track.clientWidth - 2;
          this.prev.disabled = this.track.scrollLeft <= 0;
          this.next.disabled = this.track.scrollLeft >= max;
        }

        quickAdd(button, event) {
          event.preventDefault();
          if (!button.dataset.variantId) return;
          button.classList.add('is-loading');
          addItems([{ id: button.dataset.variantId, quantity: 1 }]).finally(function () {
            button.classList.remove('is-loading');
          });
        }
      }
    );
  }

  /* ================================================== RECOMMENDATIONS */

  if (!customElements.get('hdm-recommendations')) {
    customElements.define(
      'hdm-recommendations',
      class HdmRecommendations extends HTMLElement {
        connectedCallback() {
          if (!this.dataset.url || this.dataset.loaded === 'true') return;
          this.dataset.loaded = 'true';

          fetch(this.dataset.url)
            .then(function (response) {
              if (!response.ok) throw new Error('Recommendations request failed');
              return response.text();
            })
            .then(
              function (text) {
                var doc = new DOMParser().parseFromString(text, 'text/html');
                var incoming = doc.querySelector('hdm-recommendations');
                if (incoming && incoming.innerHTML.trim()) {
                  this.innerHTML = incoming.innerHTML;
                } else {
                  this.hideSection();
                }
              }.bind(this)
            )
            .catch(
              function () {
                this.hideSection();
              }.bind(this)
            );
        }

        /** Never leave an empty section shell taking up vertical space. */
        hideSection() {
          var wrapper = this.closest('.hdm-also');
          if (wrapper) wrapper.style.display = 'none';
          this.innerHTML = '';
        }
      }
    );
  }

  /* ============================================== FEATURED REVIEW REVEAL */

  if (!customElements.get('hdm-reveal')) {
    customElements.define(
      'hdm-reveal',
      class HdmReveal extends HTMLElement {
        connectedCallback() {
          if (!MOTION_OK || !('IntersectionObserver' in window)) {
            this.classList.add('is-revealed');
            return;
          }
          var observer = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                if (!entry.isIntersecting) return;
                entry.target.classList.add('is-revealed');
                observer.unobserve(entry.target);
              });
            },
            { threshold: 0.2 }
          );
          observer.observe(this);
        }
      }
    );
  }
})();
